import { useEffect, useMemo, useState, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { studentsStore } from '../../Students/model/studentsStore'
import { ensureStudentsLoaded } from '../../Students/model/ensureStudentsLoaded'
import { fetchStudentProfile } from '../../../api/users'
import { fetchStudentApplications } from '../../../api/applications'
import { applicationsStore } from '../../ApplicationsPage/model/applicationsStore'
import { ensureApplicationsLoaded } from '../../ApplicationsPage/model/ensureApplicationsLoaded'
import { ROUTES } from '../../../shared/config/routes'

export const useStudentProfilePage = studentId => {
	const navigate = useNavigate()
	const location = useLocation()
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState(null)

	const normalizedId = studentId ? String(studentId).trim() : null

	const [profileStudentId, setProfileStudentId] = useState(normalizedId)
	const [profileData, setProfileData] = useState(null)
	const [isLoadingProfile, setIsLoadingProfile] = useState(Boolean(normalizedId))
	const [profileError, setProfileError] = useState(null)

	// Reset profile payload as soon as the URL student changes (before paint settles
	// on stale digital-profile content from the previous student).
	if (normalizedId !== profileStudentId) {
		setProfileStudentId(normalizedId)
		setProfileData(null)
		setProfileError(null)
		setIsLoadingProfile(Boolean(normalizedId))
	}

	useEffect(() => {
		let alive = true

		const load = async () => {
			try {
				setIsLoading(true)
				setError(null)
				// Always run: previous gate (empty students / missing avatarKey) skipped
				// applications load on the common Students → profile path, so PersonalData
				// showed "No universities selected" even when targets existed.
				await ensureStudentsLoaded()
				try {
					await ensureApplicationsLoaded()
				} catch (appErr) {
					console.warn(
						'Failed to load applications for student profile:',
						appErr,
					)
				}
			} catch (e) {
				console.error(e)
				if (alive) setError(e?.message || 'Failed to load students')
			} finally {
				if (alive) setIsLoading(false)
			}
		}

		load()

		return () => {
			alive = false
		}
	}, [])

	useEffect(() => {
		if (!normalizedId) return undefined

		let alive = true

		const loadProfile = async () => {
			try {
				setIsLoadingProfile(true)
				setProfileError(null)

				const data = await fetchStudentProfile(normalizedId)

				if (!alive) return
				setProfileData(data?.profile || null)
			} catch (e) {
				console.error('Error loading profile:', e)
				if (!alive) return
				setProfileError(e?.message || 'Failed to load profile')
				setProfileData(null)
			} finally {
				if (alive) setIsLoadingProfile(false)
			}
		}

		loadProfile()

		return () => {
			alive = false
		}
	}, [normalizedId])

	// Student-scoped applications refresh so PersonalData universities stay in sync
	// even when the global cohort cache was empty/stale for this student.
	useEffect(() => {
		if (!normalizedId) return undefined

		let alive = true

		const loadStudentApplications = async () => {
			try {
				const data = await fetchStudentApplications(normalizedId)
				if (!alive) return
				const apps = data?.applications || []
				const sid = String(normalizedId)
				const others = (applicationsStore.applications || []).filter(
					app => String(app?.studentId) !== sid,
				)
				applicationsStore.setApplications([...others, ...apps])
			} catch (appErr) {
				console.warn(
					'Failed to load student applications for profile:',
					appErr,
				)
			}
		}

		loadStudentApplications()

		return () => {
			alive = false
		}
	}, [normalizedId])

	const list = studentsStore.filteredStudents

	const currentStudent = useMemo(
		() => studentsStore.getStudentById(normalizedId),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[normalizedId, studentsStore.students],
	)

	const { prevId, nextId, index, total } = useMemo(
		() => studentsStore.getPrevNextIds(normalizedId, list),
		[normalizedId, list],
	)

	const prevStudent = useMemo(
		() => (prevId ? studentsStore.getStudentById(String(prevId).trim()) : null),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[prevId, studentsStore.students],
	)

	const nextStudent = useMemo(
		() => (nextId ? studentsStore.getStudentById(String(nextId).trim()) : null),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[nextId, studentsStore.students],
	)

	const goPrev = useCallback(() => {
		if (!prevId) return
		const encodedId = encodeURIComponent(String(prevId).trim())
		navigate(`${ROUTES.STUDENT_PROFILE}/${encodedId}`, {
			replace: true,
			state: location.state,
		})
	}, [prevId, navigate, location.state])

	const goNext = useCallback(() => {
		if (!nextId) return
		const encodedId = encodeURIComponent(String(nextId).trim())
		navigate(`${ROUTES.STUDENT_PROFILE}/${encodedId}`, {
			replace: true,
			state: location.state,
		})
	}, [nextId, navigate, location.state])

	const goBack = useCallback(() => {
		const backTo = location.state?.backTo
		if (backTo?.type === 'dashboard') {
			navigate(ROUTES.DASHBOARD, {
				state: {
					priorityTab: backTo.priorityTab || null,
					scrollToPriority: true,
				},
			})
			return
		}
		if (window.history.state?.idx > 0) navigate(-1)
		else navigate(ROUTES.STUDENTS)
	}, [navigate, location.state])

	return {
		isLoading,
		error,
		currentStudent,
		list,
		prevId,
		nextId,
		prevStudent,
		nextStudent,
		index,
		total,
		goPrev,
		goNext,
		goBack,
		profileData,
		isLoadingProfile,
		profileError,
	}
}
